import React, { useEffect, useRef } from 'react';
import queryString from 'query-string';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { LocalGeneralError } from '../../Shared/AlertView';
import LoadingIndicator from '../../Shared/LoadingIndicator';
import useUniversalLogin from '../UniversalLogin/useUniversalLogin';
import { useSubdomainInfo } from '../SignUp/useSubdomainInfo';
import getSubdomainFromUrl from '../../utils/getSubdomainFromUrl';
import {
  confirmSSOAccount,
  confirmSSOAccountVariables,
} from './__generated__/confirmSSOAccount';

export const SSO_CONFIRM_ACCOUNT_MUTATION = gql`
  mutation confirmSSOAccount($rid: String!) {
    confirmSSOAccount(rid: $rid)
  }
`;

const getRid = (location: Location): string | undefined => {
  const { search } = location;
  const query = queryString.parse(search);
  const { rid } = query;

  return rid as string;
};

const ConfirmSSOAccount = () => {
  const rid = getRid(location);
  const {
    authProviderOrganisationId,
    loading: subdomainLoading,
    error: subdomainError,
  } = useSubdomainInfo({
    subdomain: getSubdomainFromUrl(),
  });

  const { loginWithUniversalLogin } = useUniversalLogin();

  const [confirmAccount, { error: confirmationError }] = useMutation<
    confirmSSOAccount,
    confirmSSOAccountVariables
  >(SSO_CONFIRM_ACCOUNT_MUTATION, {
    onCompleted: async () =>
      loginWithUniversalLogin({
        authProviderOrganisationId: authProviderOrganisationId,
      }),
  });

  const error = !rid || subdomainError || confirmationError;
  const initialising = subdomainLoading;

  // Guards against firing a second confirmSSOAccount request for the same
  // rid if this effect re-runs before the first request has resolved.
  const hasConfirmedRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!error && !initialising && hasConfirmedRef.current !== rid) {
      hasConfirmedRef.current = rid;
      void confirmAccount({
        variables: {
          rid: rid || '',
        },
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rid, initialising, error]);

  if (error) {
    return <LocalGeneralError />;
  }

  return <LoadingIndicator />;
};

export default ConfirmSSOAccount;
